import React from "react"
import {
  Box, Text, VStack, Menu, MenuButton, MenuList, MenuItem, IconButton,
} from "@chakra-ui/react"
import { useHistory, useParams } from "react-router-dom"
import Icon from "components/elements/Icon"
import * as api from "api/badges"
import { useMutation, useQueryClient } from "react-query"
import { PrinterModelLabels } from "components/BadgeBuilder/utils/printers"
import { ModalName } from "sharedTypes"
import { eventBadgeTemplateEditPath } from "utilities/routes"
import { generateDuplicateName } from "utilities/strings"
import { successToast } from "utilities/toasts"
import useModal from "services/useModal"
import ConfirmDelete from "components/dialogs/Confirm/ConfirmDelete"
import { BadgeTemplate } from "sharedTypes/badge"
import useMeasure from "react-use-measure"
import { addBadge, removeBadge, updateBadge } from "context/actions"
import BadgePreviewRenderer from "./BadgePreviewRenderer"

type Props = {
  template: BadgeTemplate
  isSelected?: boolean
  onClick: () => void
  showActions?: boolean
}

const BadgeTemplateCard = ({
  template,
  isSelected,
  onClick,
  showActions = true,
}: Props) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { eventId } = useParams<{ eventId: string }>()
  const showModal = useModal()
  const [previewContainerRef, { width: containerWidth, height: containerHeight }] = useMeasure()

  const deleteBadge = useMutation(
    () => api.deleteBadge(eventId, template.id),
    {
      onSuccess: () => {
        removeBadge(queryClient, eventId, template.id)
        successToast({
          title: "Badge removed from event",
        })
      },
    },
  )

  const duplicateBadge = useMutation(
    () => api.postDuplicateBadge(eventId, template.id).then(({ data: duplicatedTemplate }) => {
      const newName = generateDuplicateName(template.name, {
        suffix: "copy",
      })

      return api.putBadge(eventId, duplicatedTemplate.id,
        { name: newName }).then(({ data }) => data)
    }),
    {
      onSuccess: (data) => {
        addBadge(queryClient, eventId, data)
        successToast({
          title: "Badge duplicated",
        })
      },
    },
  )

  const handleOpenInBadgeBuilder = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (template && eventId) {
      history.push(eventBadgeTemplateEditPath(eventId, template.id))
    }
  }

  const handleDelete = () => {
    deleteBadge.mutate()
  }

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    duplicateBadge.mutate()
  }

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (template) {
      showModal(ModalName.RenameBadgeTemplate, {
        initialValues: { name: template.name },
        onSubmit: (values: { name: string }) => api.putBadge(
          eventId, template.id, { name: values.name.trim() },
        ).then(({ data }) => {
          updateBadge(queryClient, eventId, data)
          successToast({
            title: "Badge renamed",
          })

          return data
        }),
      })
    }
  }

  const isMeasured = containerWidth > 0 && containerHeight > 0

  const getPreviewDimensions = () => {
    if (!template || !isMeasured) {
      return { width: 0, height: 0 }
    }

    const templateAspectRatio = template.widthMm / template.heightMm
    const containerAspectRatio = containerWidth / containerHeight

    const padding = 32
    const availableWidth = containerWidth - padding * 2
    const availableHeight = containerHeight - padding * 2

    // Fit template into available space while maintaining aspect ratio
    if (templateAspectRatio > containerAspectRatio) {
      // Template is wider than container, constrain by width
      const width = Math.min(availableWidth, 200)
      const height = width / templateAspectRatio

      return { width, height }
    }

    const height = Math.min(availableHeight, 150)
    const width = height * templateAspectRatio

    return { width, height }
  }

  return (
    <Box
      w="full"
      aspectRatio={1.3}
      borderWidth={1}
      borderRadius="lg"
      cursor="pointer"
      _hover={{ shadow: "md" }}
      transition="all 0.1s"
      onClick={onClick}
      bg="white"
      position="relative"
      h="full"
      display="flex"
      flexDirection="column"
    >
      {showActions && (
      <Box position="absolute" top={2} right={2} zIndex={500}>
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<Icon icon="more" size={3} color="gray.600" />}
            size="sm"
            variant="ghost"
            aria-label="Template actions"
            onClick={(e) => e.stopPropagation()}
          />
          <MenuList zIndex={500}>
            <MenuItem
              icon={<Icon icon="badges" />}
              onClick={handleOpenInBadgeBuilder}
              color="blue.600"
              _hover={{ bg: "blue.50" }}
            >
              Edit in Badge Builder
            </MenuItem>
            <MenuItem icon={<Icon icon="copy" />} onClick={handleDuplicate}>
              Duplicate
            </MenuItem>
            <MenuItem icon={<Icon icon="pencil" />} onClick={handleRename}>
              Rename
            </MenuItem>
            <ConfirmDelete
              type="Badge Template"
              name={template.name}
              onClickConfirm={handleDelete}
              description="This action cannot be undone."
              stopChildClickPropagation
            >
              <MenuItem
                icon={<Icon icon="trash" />}
                color="red.600"
                _hover={{ bg: "red.50" }}
              >
                Delete
              </MenuItem>
            </ConfirmDelete>
          </MenuList>
        </Menu>
      </Box>
      )}

      <Box
        ref={previewContainerRef}
        p={4}
        textAlign="center"
        bg={isSelected ? "blue.50" : "gray.50"}
        borderTopRadius="md"
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={0}
        maxHeight="calc(100% - 60px)"
        overflow="hidden"
      >
        <Box
          opacity={isMeasured ? 1 : 0}
          transition="opacity 150ms ease-out"
        >
          {isMeasured && (
            <BadgePreviewRenderer
              template={template}
              containerWidth={getPreviewDimensions().width}
              containerHeight={getPreviewDimensions().height}
            />
          )}
        </Box>
      </Box>

      <Box
        p={3}
        borderTopWidth={1}
        borderBottomRadius="md"
        flexShrink={0}
      >
        <VStack spacing={0} align="start">
          <Text fontSize="base" fontWeight="semibold" noOfLines={1}>
            {template.name}
          </Text>
          <VStack spacing={0} align="start" fontSize="xs">
            <Text color="gray.600" noOfLines={1}>
              {PrinterModelLabels[template.printerModel]} ({template.widthMm}x{template.heightMm}mm)
            </Text>
          </VStack>
        </VStack>
      </Box>
    </Box>
  )
}

export default BadgeTemplateCard
